/**
 * The panel behind the toolbar's square: Typ, Tryb, Dni, and — over the clients' plans only —
 * Klient.
 *
 * ── Base UI supplies the popup; every visual byte is ours ────────────────────────────────────
 * `Popover` gives the dismissal, the ARIA wiring, the portal and the focus return. The skin is this
 * file's stylesheet, transcribed from the specimen with `filtersOpen`.
 *
 * ── It hangs over the list card's TOP-LEFT corner, which is where the canon puts it ──────────
 * The specimen anchors variant `d` at `{ top: 150.5px, left: 24px }` in a 1512-wide frame whose list
 * card stands at `{ 30, 131 }` — so the panel overlaps the card's own head by 19.5px and hangs 6px
 * past its left edge. Anchoring to the CARD and restating those two offsets reproduces the
 * relationship at any width, which is what the app needs and the frame's absolute numbers are not.
 *
 * `collisionAvoidance: none` on both axes, deliberately: the panel is 342.5px inside a 461px pane
 * that starts at the measure's own inset, so it cannot overflow anything, and a positioner that
 * "helped" would move a box the pixel gate measures.
 *
 * ── `modal="trap-focus"`, and the backdrop is the canon's scrim rather than a modality ───────
 * The value traps focus and does nothing else: page scroll stays live and a press outside still
 * dismisses. Full `modal` would lock the document's scroll, and this repository has measured what a
 * scroll lock does to a screen it was told not to move. The visible scrim is `Popover.Backdrop`,
 * which is also what catches the press outside, so the canon's `onClick={toggleFilters}` on its own
 * scrim is Base UI's dismissal rather than a second handler.
 *
 * The ✕ is inside the popup because Base UI asks for one whenever focus is trapped — a touch screen
 * reader needs an exit — and because the canon draws it. It is the same control `PlanMetaPopover`
 * puts in the same corner.
 *
 * ── The facets apply as they are pressed; `Pokaż N` only closes ──────────────────────────────
 * There is no draft and no apply step. The list behind the scrim narrows under each press, the
 * footer's figure counts what would show, and the button's whole job is to put the trainer back on
 * the list they have just narrowed. That is the canon's own wiring (`fpApply`'s handler is
 * `toggleFilters`), and it is why the panel needs no local copy of the filter.
 *
 * ── The Dni SLIDER is one control, and the numbers under it are its scale ────────────────────
 * A native `<input type="range">` drives the facet, so the keyboard path, the value announcement and
 * the drag are the platform's. The canon makes each of its seven numbers pressable as well; here
 * they are the scale printed under the rail, `aria-hidden` and unpressable, because a second control
 * over one value is a second thing to keep in step and the range already reaches every value it
 * offers. Visually they are the specimen's, filled pill and all.
 */

import { Popover } from '@base-ui/react/popover';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PLAN_CATEGORY_ORDER } from '~/entities/plan-draft';
import photoBox from '~/shared/ui/photoBox.module.css';
import { planCategoryColour } from '~/shared/ui/planCategoryColour';

import {
  NO_DAY_LIMIT,
  clearFilters,
  facetBinds,
  toggleCategory,
  toggleClient,
  type ClientFacetOption,
  type PlanFilterFacet,
  type PlanLibraryFilter,
  type PlanModeFacet,
} from '../model/planLibraryFilter';
import type { PlanScope } from '../model/planScope';
import { personName } from '../model/templateFollowers';
import { CalendarModeIcon, CategoryIcon, SelfPacedModeIcon } from './PlanLibraryIcons';
import shell from '~/shared/ui/filterPanel.module.css';
import styles from './FilterPanel.module.css';

/**
 * Which body the panel is showing.
 *
 * **A tab is offered where its facet can BIND.** Klient over the clients' plans only, because a
 * template has no client; Dni over the templates only, because `PlanAssignmentSummary` carries no
 * day count of any kind and answering it from a fork's `sourceTemplateId` would be a fabrication.
 * The canon draws Dni in both scopes; a slider that moved a list nobody could narrow is the dead
 * control owner decision D2 already refused once, and D13 records this one.
 *
 * The FACET survives the switch either way, which is the owner's own rule: Dni set on the templates
 * is still set when the trainer comes back, and the footer's `Wyczyść` reaches it from both scopes.
 */
type FilterTab = PlanFilterFacet;

/** The three modes, in the canon's order, with the mark each one wears. */
const MODES: readonly {
  readonly mode: PlanModeFacet;
  readonly labelKey: string;
  readonly Icon: typeof CalendarModeIcon | null;
}[] = [
  { mode: 'all', labelKey: 'web:planLibrary.filters.modeAll', Icon: null },
  // The rows' own two words, not a second pair: owner decision D21 gives one mode one name.
  { mode: 'calendar', labelKey: 'web:planLibrary.mode.calendar', Icon: CalendarModeIcon },
  { mode: 'selfPaced', labelKey: 'web:planLibrary.mode.selfPaced', Icon: SelfPacedModeIcon },
];

const DAY_TICKS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];

interface FilterPanelProps {
  readonly scope: PlanScope;
  readonly filter: PlanLibraryFilter;
  readonly onFilterChange: (filter: PlanLibraryFilter) => void;
  /** Every client with a live plan, whatever the current selection is. Empty outside that scope. */
  readonly clientOptions: readonly ClientFacetOption[];
  /** How many rows the list is showing right now — the footer's figure. */
  readonly matchCount: number;
  /** The list card, which the panel hangs off. See the header. */
  readonly anchor: React.RefObject<HTMLElement | null>;
  readonly children: React.ReactNode;
}

/** The panel's top edge, measured INSIDE the card's own top edge. See the header. */
const PANEL_INSET_TOP = 19.5;
/** And its left edge, measured OUTSIDE the card's. */
const PANEL_INSET_LEFT = -6;

export function FilterPanel({
  scope,
  filter,
  onFilterChange,
  clientOptions,
  matchCount,
  anchor,
  children,
}: FilterPanelProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<FilterTab>('type');
  const clients = scope === 'clients';
  // A panel reopened after a scope switch must never come back on a body that scope does not offer.
  // `facetBinds` is `model/`'s own rule and the toolbar's dot counts by the same one, so a tab that
  // is not offered cannot be a facet the square claims is on.
  const active: FilterTab = facetBinds(tab, scope) ? tab : 'type';

  const tabs: readonly { readonly id: FilterTab; readonly label: string; readonly count: string }[] =
    [
      {
        id: 'type',
        label: t('web:planLibrary.filters.tabType'),
        count: filter.categories.length === 0 ? '' : String(filter.categories.length),
      },
      {
        id: 'mode',
        label: t('web:planLibrary.filters.tabMode'),
        count: filter.mode === 'all' ? '' : '1',
      },
      ...(clients
        ? [
            {
              id: 'client' as const,
              label: t('web:planLibrary.filters.tabClient'),
              count: filter.clients.length === 0 ? '' : String(filter.clients.length),
            },
          ]
        : [
            {
              id: 'days' as const,
              label: t('web:planLibrary.filters.tabDays'),
              // The canon's own `≤4`: the facet's VALUE rather than a number of choices.
              count:
                filter.maxDays < NO_DAY_LIMIT
                  ? t('web:planLibrary.filters.daysAtMost', { count: filter.maxDays })
                  : '',
            },
          ]),
    ];

  return (
    <Popover.Root modal="trap-focus">
      {children}
      <Popover.Portal>
        <Popover.Backdrop className={shell.scrim} />
        <Popover.Positioner
          className={shell.positioner}
          anchor={anchor}
          side="bottom"
          align="start"
          // The panel's top edge sits INSIDE the card's, so the offset is the card's own height less
          // the inset — the only way to express "hang over its top-left corner" against an anchor
          // the positioner measures from the outside.
          sideOffset={({ anchor: box }) => PANEL_INSET_TOP - box.height}
          alignOffset={PANEL_INSET_LEFT}
          collisionAvoidance={{ side: 'none', align: 'none' }}
        >
          <Popover.Popup className={shell.panel}>
            <div className={shell.head}>
              <Popover.Title className={shell.title}>
                {t('web:planLibrary.filters.title')}
              </Popover.Title>
              <span className={shell.spacer} aria-hidden />
              <Popover.Close
                className={shell.close}
                aria-label={t('web:planLibrary.filters.close')}
              >
                <span aria-hidden>✕</span>
              </Popover.Close>
            </div>

            {/* A real tab list: one tab stop, arrows between the tabs, and each body labelled by the
              * tab that opened it. That contract IS what this control does — the bodies are
              * alternative views of one panel, nothing navigates, and nothing is lost by switching. */}
            <div className={shell.tabs} role="tablist" aria-label={t('web:planLibrary.filters.title')}>
              {tabs.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  role="tab"
                  id={`plan-filter-tab-${entry.id}`}
                  aria-controls={`plan-filter-body-${entry.id}`}
                  aria-selected={active === entry.id}
                  tabIndex={active === entry.id ? 0 : -1}
                  className={`${String(shell.tab)} ${active === entry.id ? String(shell.tabOn) : ''}`}
                  onClick={() => {
                    setTab(entry.id);
                  }}
                  onKeyDown={(event) => {
                    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
                    if (delta === 0) return;
                    event.preventDefault();
                    const at = tabs.findIndex((candidate) => candidate.id === active);
                    // Wraps at both ends, which is the tab pattern's own rule and the opposite of
                    // the LIST's — a tab strip is a ring of four, a list is a column of rows.
                    const next = tabs[(at + delta + tabs.length) % tabs.length];
                    if (next === undefined) return;
                    setTab(next.id);
                    document.getElementById(`plan-filter-tab-${next.id}`)?.focus();
                  }}
                >
                  {entry.label}
                  {entry.count === '' ? null : (
                    <b className={shell.tabCount}>{entry.count}</b>
                  )}
                </button>
              ))}
            </div>

            <div
              className={shell.body}
              role="tabpanel"
              id={`plan-filter-body-${active}`}
              aria-labelledby={`plan-filter-tab-${active}`}
            >
              {active === 'type' ? (
                <TypeBody filter={filter} onFilterChange={onFilterChange} />
              ) : active === 'mode' ? (
                <ModeBody filter={filter} onFilterChange={onFilterChange} />
              ) : active === 'days' ? (
                <DaysBody filter={filter} onFilterChange={onFilterChange} />
              ) : (
                <ClientBody
                  filter={filter}
                  onFilterChange={onFilterChange}
                  options={clientOptions}
                />
              )}
            </div>

            <div className={shell.foot}>
              <button
                type="button"
                className={shell.clear}
                onClick={() => {
                  onFilterChange(clearFilters(filter));
                }}
              >
                {t('web:planLibrary.filters.clear')}
              </button>
              <span className={shell.spacer} aria-hidden />
              {/* Closes, and nothing else — the facets are already applied. The figure is what the
                * list is showing behind the scrim. */}
              <Popover.Close className={shell.apply}>
                {t('web:planLibrary.filters.apply')}
                <b className={shell.applyCount}>{matchCount}</b>
              </Popover.Close>
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

interface BodyProps {
  readonly filter: PlanLibraryFilter;
  readonly onFilterChange: (filter: PlanLibraryFilter) => void;
}

/**
 * Typ — six chips, multi-select, each in its own category's ink.
 *
 * Checkboxes rather than buttons: the trainer is choosing a SET, several can be on at once, and a
 * pressed button would have to claim `aria-pressed` to say the same thing less precisely. The input
 * is off screen and the label around it is the chip, which is `ScopeSwitch`'s pattern for the same
 * reason.
 */
function TypeBody({ filter, onFilterChange }: BodyProps) {
  const { t } = useTranslation();

  return (
    <>
      <span className={shell.sectionTitle}>{t('web:planLibrary.filters.typeTitle')}</span>
      <div className={styles.chips}>
        {/* `PLAN_CATEGORY_ORDER` — owner decision D14, 3 September 2026: ONE order for the six
          * categories in the whole app. The canon disagrees with itself between two of its own
          * controls (its toolbar's `CAT` and this panel's `TYPE_KEYS`), and the panel therefore
          * departs from its own specimen deliberately: the chips wrap 4 + 2 here where the
          * specimen wraps 3 + 3. The create modal and the builder's meta popover already draw this
          * order. */}
        {PLAN_CATEGORY_ORDER.map((category) => {
          const on = filter.categories.includes(category);
          return (
            <label
              key={category}
              className={`${String(styles.chip)} ${on ? String(styles.chipOn) : ''}`}
              style={planCategoryColour(category)}
            >
              <input
                type="checkbox"
                className={styles.control}
                checked={on}
                onChange={() => {
                  onFilterChange(toggleCategory(filter, category));
                }}
              />
              <CategoryIcon category={category} className={styles.chipIcon} />
              {t(`plans.category.${category}`)}
              {/* The tick is the canon's second channel beside the fill and the ink; the checkbox's
                * own state is what a reader hears. */}
              {on ? <b className={styles.chipCheck} aria-hidden>✓</b> : null}
            </label>
          );
        })}
      </div>
    </>
  );
}

/** Tryb — one of three, so a radio group. */
function ModeBody({ filter, onFilterChange }: BodyProps) {
  const { t } = useTranslation();

  return (
    <>
      <span className={shell.sectionTitle}>{t('web:planLibrary.filters.modeTitle')}</span>
      <div className={styles.modes}>
        {MODES.map(({ mode, labelKey, Icon }) => {
          const on = filter.mode === mode;
          return (
            <label
              key={mode}
              className={`${String(styles.mode)} ${on ? String(styles.modeOn) : ''}`}
            >
              <input
                type="radio"
                name="plan-filter-mode"
                className={styles.control}
                checked={on}
                onChange={() => {
                  onFilterChange({ ...filter, mode });
                }}
              />
              {Icon === null ? null : <Icon className={styles.modeIcon} />}
              {t(labelKey)}
              <span className={shell.spacer} aria-hidden />
              {on ? <b className={styles.modeCheck} aria-hidden>✓</b> : null}
            </label>
          );
        })}
      </div>
    </>
  );
}

/** Dni — one native range, and the canon's seven numbers printed under it as its scale. */
function DaysBody({ filter, onFilterChange }: BodyProps) {
  const { t } = useTranslation();
  // The rail's fill and the handle's position, as one number the stylesheet reads.
  const fill = `${String(((filter.maxDays - 1) / (NO_DAY_LIMIT - 1)) * 100)}%`;

  return (
    <>
      <span className={shell.sectionTitle} id="plan-filter-days-title">
        {t('web:planLibrary.filters.daysTitle')}
      </span>
      <div className={styles.slider} style={{ ['--pl-days' as string]: fill }}>
        <span className={styles.readout}>
          <b className={styles.readoutFigure}>{filter.maxDays}</b>
          {t('web:planLibrary.filters.daysUnit', { count: filter.maxDays })}
          <span className={styles.readoutMax}>{t('web:planLibrary.filters.daysMax')}</span>
        </span>
        {/* The rail and its fill are drawn; the range lies transparently over them and is the
          * control. See the stylesheet for why the fill cannot ride on the track pseudo-element. */}
        <span className={styles.track}>
          <span className={styles.trackFill} aria-hidden />
          <input
            type="range"
            className={styles.range}
            min={1}
            max={NO_DAY_LIMIT}
            step={1}
            value={filter.maxDays}
            aria-labelledby="plan-filter-days-title"
            onChange={(event) => {
              onFilterChange({ ...filter, maxDays: Number(event.target.value) });
            }}
          />
        </span>
        {/* The scale, not a second control — see the file header. */}
        <span className={styles.ticks} aria-hidden>
          {DAY_TICKS.map((day) => (
            <span
              key={day}
              className={`${String(styles.tick)} ${
                day === filter.maxDays ? String(styles.tickOn) : ''
              }`}
            >
              {day}
            </span>
          ))}
        </span>
      </div>
    </>
  );
}

interface ClientBodyProps extends BodyProps {
  readonly options: readonly ClientFacetOption[];
}

/** Klient — every client with a live plan, multi-select, in the list's own surname order. */
function ClientBody({ filter, onFilterChange, options }: ClientBodyProps) {
  const { t } = useTranslation();

  return (
    <>
      <span className={shell.sectionTitle}>{t('web:planLibrary.filters.clientTitle')}</span>
      <div className={styles.clients}>
        {options.map((option) => {
          const on = filter.clients.includes(option.customerId);
          return (
            <label
              key={option.customerId}
              className={`${String(styles.client)} ${on ? String(styles.clientOn) : ''}`}
            >
              <input
                type="checkbox"
                className={styles.control}
                checked={on}
                onChange={() => {
                  onFilterChange(toggleClient(filter, option.customerId));
                }}
              />
              {/* NOT `shared/ui/AvatarChip`. That control is 32px and its own header refuses a
                * `className` that restyles it; the canon draws this disc at 22.5 inside a 37px row,
                * where a 32px one does not fit. It is the strip's situation exactly — a third
                * diameter with a skin of its own — and it is drawn here for the same reason. */}
              {option.avatarUrl === null ? (
                <i className={styles.clientAvatar} aria-hidden>
                  {option.initials}
                </i>
              ) : (
                <img
                  className={`${String(photoBox.photo)} ${String(styles.clientAvatar)}`}
                  src={option.avatarUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              )}
              <span className={styles.clientBody}>
                <b className={styles.clientName}>{personName(option.fullName, t)}</b>
                <b className={styles.clientSub}>
                  {t('web:planLibrary.filters.clientPlans', { count: option.planCount })}
                </b>
              </span>
              {on ? <b className={styles.clientCheck} aria-hidden>✓</b> : null}
            </label>
          );
        })}
      </div>
    </>
  );
}
